<template>
    <b-tooltip
        class="footnote-tooltip"
        :label="footnoteLabel"
        :delay="tooltipOptions.delay"
        :type="tooltipOptions.style"
    >
        <a
            :href="`#note-${footnoteNumber}`"
            @mouseenter="setLabel"
        >{{ footnoteNumber }}</a>
    </b-tooltip>
</template>

<script>
import unescape from 'lodash.unescape';

export default {
    props: {
        footnoteNumber: {
            type: Number,
            required: true,
        },
        tooltipOptions: {
            type: Object,
            required: false,
            default: () => ({
                delay: 300,
                style: 'is-light',
                truncateLength: 220,
                moreInfoText: 'Click for more details/links.',
            }),
        },
    },
    data: () => ({
        footnoteLabel: '',
    }),
    methods: {
        /**
         * Get, format, and set the footnote label.
         */
        setLabel() {
            if (!this.footnoteLabel) {
                let note = this.getNote();
                note = this.replaceHtmlTags(note);
                note = unescape(note);
                note = this.truncateNote(note);
                this.footnoteLabel = `${note} ${this.tooltipOptions.moreInfoText}`;
            }
        },
        /**
         * Get the innerHTML for the footnote directly from the report notes section
         *
         * @returns {string} - html of the footnote
         */
        getNote() {
            return document.querySelector(`#note-${this.footnoteNumber} .note-text`).innerHTML;
        },
        /**
         * Shorten urls that are too long or remove footnotes. We shorten urls by hiding the middle portion, and ensure
         * that the hidden portion is bookended by slashes.
         *
         * @param {string} note - note containing links - must include the html link tags
         * @returns {string} - the given note with link tags removed and with all links shortened
         */
        formatLinks(note) {
            let newNote = note;
            // get all links in the note
            const linksHtml = newNote.match(/<a[^>]*>[^<>]*<\/a>/) || [];
            linksHtml.forEach((linkHtml) => {
                // remove any footnotes
                if (linkHtml.includes('href="#note-')) {
                    newNote = newNote.replace(linkHtml, '');
                } else {
                    // remove link tags
                    let link = linkHtml.replace(/<([^<>]*)>/g, '').trim();

                    // check if link is more characters than can be shown on a single line
                    if (link.length > 55) {
                        // Get beginning chunk of url to show - add 1 to include the slash
                        const urlStart = link.substring(0, 27).lastIndexOf('/') + 1;
                        // Get end chunk of url to show
                        const urlEnd = (link.length - 27) + link.substring(link.length - 27).indexOf('/');
                        // build new link to show
                        link = `${link.substring(0, urlStart)}...${link.substring(urlEnd)}`;
                    }

                    newNote = newNote.replace(linkHtml, link);
                }
            });
            return newNote;
        },
        /**
         * Replace all html tags in a string with empty string or a dash.
         *
         * @param {string} note - string that may contain html tags
         * @returns {string} - string with html tags removed or replaced with dashes
         */
        replaceHtmlTags(note) {
            let newNote = note;

            // replace list item tags with dashes
            newNote = newNote.replace(/<li>/g, ' - ').replace(/<\/li>/g, '');

            newNote = this.formatLinks(newNote);

            // remove any remaining html tags
            newNote = newNote.replace(/<([^<>]*)>/g, '');

            return newNote;
        },
        /**
         * If the note is longer than the given truncateLength, truncate it and add an ellipsis
         *
         * @param {string} note - note string of unknown length
         * @returns {string} - note string that is less than truncateLength
         */
        truncateNote(note) {
            return note.length > this.tooltipOptions.truncateLength
                ? `${note.substring(0, this.tooltipOptions.truncateLength).trim().replace(/[,.;:]$/, '')}...`
                : note;
        },
    },
};
</script>

<style lang="scss">
.footnote-tooltip.b-tooltip {
    $secondary: #3195f7 !default;

    a {
        // styling for the superscript number
        color: $secondary;
    }

    &::after {
        // styling for the tooltip
        white-space: normal;
        width: 30em;
        text-align: center;
    }
}
</style>
